import { alpha, createTheme } from "@mui/material/styles";

const serifFont = '"Texturina Latin", "Eb Garamond Latin", Georgia, serif';
const bodyFont = '"Texturina Latin", "Eb Garamond Latin", Georgia, serif';

const colors = {
	bgBlack: "#040610",
	bgNavy: "#071126",
	bgPurple: "#17102d",
	panel: "#090d1b",
	panelRaised: "#111a2e",
	gold: "#d8b45a",
	goldBright: "#ffe3a1",
	arcaneBlue: "#58c7ff",
	arcanePurple: "#8d6dff",
	textPrimary: "#f6f4eb",
	textSecondary: "#aebde3",
	border: "#5f6e9e",
	borderDim: "#33405f",
	error: "#ff9363",
	success: "#7de0a6",
	warning: "#f0cf67",
} as const;

const focusRing = `0 0 0 2px ${alpha(colors.bgBlack, 0.9)}, 0 0 0 4px ${alpha(colors.arcaneBlue, 0.86)}`;
const panelGradient = `linear-gradient(180deg, ${alpha(colors.panelRaised, 0.96)} 0%, ${alpha(colors.panel, 0.97)} 100%)`;

export const movesofconsequencePanelSx = {
	border: `1px solid ${alpha(colors.gold, 0.34)}`,
	borderRadius: 2,
	background: panelGradient,
	boxShadow: `0 18px 42px ${alpha("#000", 0.36)}, inset 0 1px 0 ${alpha("#fff", 0.05)}`,
	color: colors.textPrimary,
};

export const movesofconsequenceSelectableSx = {
	border: `1px solid ${alpha(colors.border, 0.55)}`,
	borderRadius: 1,
	background: alpha(colors.panelRaised, 0.64),
	color: colors.textPrimary,
};

export const movesofconsequenceMuiTheme = createTheme({
	cssVariables: true,
	palette: {
		mode: "dark",
		primary: {
			main: colors.gold,
			light: colors.goldBright,
			dark: "#9f7831",
			contrastText: colors.bgBlack,
		},
		secondary: {
			main: colors.arcanePurple,
			light: "#c0adff",
			dark: "#5d46a8",
			contrastText: colors.textPrimary,
		},
		info: {
			main: colors.arcaneBlue,
			light: "#9adfff",
			dark: "#277eb4",
			contrastText: colors.bgBlack,
		},
		success: {
			main: colors.success,
			contrastText: colors.bgBlack,
		},
		warning: {
			main: colors.warning,
			contrastText: colors.bgBlack,
		},
		error: {
			main: colors.error,
			contrastText: colors.bgBlack,
		},
		background: {
			default: colors.bgBlack,
			paper: colors.panel,
		},
		text: {
			primary: colors.textPrimary,
			secondary: colors.textSecondary,
		},
		divider: alpha(colors.gold, 0.22),
	},
	shape: {
		borderRadius: 8,
	},
	spacing: 8,
	typography: {
		fontFamily: bodyFont,
		h1: { fontFamily: serifFont, fontWeight: 500 },
		h2: { fontFamily: serifFont, fontWeight: 500 },
		h3: { fontFamily: serifFont, fontWeight: 500 },
		h4: { fontFamily: serifFont, fontWeight: 600 },
		h5: { fontFamily: serifFont, fontWeight: 600 },
		h6: { fontFamily: serifFont, fontWeight: 600 },
		button: {
			fontFamily: serifFont,
			fontWeight: 700,
			letterSpacing: 0,
			textTransform: "none",
		},
		overline: {
			fontFamily: serifFont,
			fontWeight: 700,
			letterSpacing: "0.12em",
			textTransform: "uppercase",
		},
	},
	shadows: [
		"none",
		`0 2px 8px ${alpha("#000", 0.26)}`,
		`0 5px 14px ${alpha("#000", 0.3)}`,
		`0 8px 22px ${alpha("#000", 0.34)}`,
		`0 12px 30px ${alpha("#000", 0.38)}`,
		`0 16px 38px ${alpha("#000", 0.42)}`,
		`0 20px 46px ${alpha("#000", 0.46)}`,
		`0 24px 54px ${alpha("#000", 0.5)}`,
		`0 28px 62px ${alpha("#000", 0.54)}`,
		`0 32px 70px ${alpha("#000", 0.58)}`,
		`0 36px 78px ${alpha("#000", 0.62)}`,
		`0 40px 86px ${alpha("#000", 0.64)}`,
		`0 44px 94px ${alpha("#000", 0.66)}`,
		`0 48px 102px ${alpha("#000", 0.68)}`,
		`0 52px 110px ${alpha("#000", 0.7)}`,
		`0 56px 118px ${alpha("#000", 0.72)}`,
		`0 60px 126px ${alpha("#000", 0.74)}`,
		`0 64px 134px ${alpha("#000", 0.76)}`,
		`0 68px 142px ${alpha("#000", 0.78)}`,
		`0 72px 150px ${alpha("#000", 0.8)}`,
		`0 76px 158px ${alpha("#000", 0.82)}`,
		`0 80px 166px ${alpha("#000", 0.84)}`,
		`0 84px 174px ${alpha("#000", 0.86)}`,
		`0 88px 182px ${alpha("#000", 0.88)}`,
		`0 92px 190px ${alpha("#000", 0.9)}`,
	],
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					background:
						"radial-gradient(circle at 50% 0%, rgba(60, 42, 91, 0.38), rgba(4, 6, 16, 1) 48%)",
					color: colors.textPrimary,
				},
				"*, *::before, *::after": {
					boxSizing: "border-box",
				},
				"@media (prefers-reduced-motion: reduce)": {
					"*, *::before, *::after": {
						animationDuration: "0.01ms !important",
						animationIterationCount: "1 !important",
						scrollBehavior: "auto !important",
						transitionDuration: "0.01ms !important",
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: panelGradient,
					borderColor: alpha(colors.gold, 0.32),
					color: colors.textPrimary,
				},
				outlined: {
					borderColor: alpha(colors.gold, 0.32),
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					...movesofconsequencePanelSx,
				},
			},
		},
		MuiButton: {
			defaultProps: {
				disableElevation: true,
			},
			styleOverrides: {
				root: {
					borderRadius: 8,
					minHeight: 36,
					paddingInline: 14,
					transition: "background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
					"&:focus-visible": {
						boxShadow: focusRing,
					},
				},
				contained: {
					background: `linear-gradient(180deg, ${colors.goldBright} 0%, ${colors.gold} 100%)`,
					border: `1px solid ${alpha(colors.goldBright, 0.68)}`,
					color: colors.bgBlack,
					"&:hover": {
						background: `linear-gradient(180deg, #fff0b8 0%, ${colors.gold} 100%)`,
						boxShadow: `0 0 18px ${alpha(colors.gold, 0.34)}`,
					},
					"&.Mui-disabled": {
						background: "#303344",
						borderColor: "#596074",
						color: "#d8dce8",
					},
				},
				outlined: {
					borderColor: alpha(colors.gold, 0.48),
					color: colors.goldBright,
					backgroundColor: alpha(colors.bgNavy, 0.46),
					"&:hover": {
						borderColor: alpha(colors.goldBright, 0.78),
						backgroundColor: alpha(colors.gold, 0.12),
					},
				},
				text: {
					color: colors.goldBright,
					"&:hover": {
						backgroundColor: alpha(colors.gold, 0.1),
					},
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					color: colors.goldBright,
					borderRadius: 8,
					"&:hover": {
						backgroundColor: alpha(colors.gold, 0.12),
					},
					"&:focus-visible": {
						boxShadow: focusRing,
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					color: alpha(colors.textSecondary, 0.92),
					"&.Mui-focused": {
						color: colors.goldBright,
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					backgroundColor: alpha(colors.bgNavy, 0.72),
					borderRadius: 8,
					color: colors.textPrimary,
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: alpha(colors.border, 0.72),
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: alpha(colors.goldBright, 0.66),
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: colors.arcaneBlue,
						borderWidth: 1,
						boxShadow: `0 0 0 2px ${alpha(colors.arcaneBlue, 0.22)}`,
					},
				},
				input: {
					"&::placeholder": {
						color: alpha(colors.textSecondary, 0.72),
						opacity: 1,
					},
				},
			},
		},
		MuiFilledInput: {
			styleOverrides: {
				root: {
					backgroundColor: alpha(colors.bgNavy, 0.78),
					border: `1px solid ${alpha(colors.border, 0.64)}`,
					borderRadius: 8,
					"&::before, &::after": {
						display: "none",
					},
					"&:hover": {
						backgroundColor: alpha(colors.panelRaised, 0.9),
					},
					"&.Mui-focused": {
						borderColor: colors.arcaneBlue,
						boxShadow: `0 0 0 2px ${alpha(colors.arcaneBlue, 0.22)}`,
					},
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					color: alpha(colors.textSecondary, 0.82),
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: {
					color: colors.textPrimary,
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				paper: {
					border: `1px solid ${alpha(colors.gold, 0.34)}`,
					backgroundImage: panelGradient,
				},
				option: {
					"&.Mui-focused": {
						backgroundColor: alpha(colors.gold, 0.13),
					},
					"&[aria-selected='true']": {
						backgroundColor: alpha(colors.arcanePurple, 0.34),
					},
				},
				tag: {
					borderColor: alpha(colors.gold, 0.42),
					backgroundColor: alpha(colors.bgNavy, 0.74),
					color: colors.textPrimary,
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					...movesofconsequencePanelSx,
					"&::before": {
						display: "none",
					},
					"&.Mui-expanded": {
						margin: 0,
					},
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					color: colors.goldBright,
					"&.Mui-focusVisible": {
						boxShadow: focusRing,
					},
				},
				expandIconWrapper: {
					color: colors.goldBright,
				},
			},
		},
		MuiLinearProgress: {
			styleOverrides: {
				root: {
					borderRadius: 999,
					backgroundColor: alpha(colors.borderDim, 0.62),
				},
				bar: {
					background: `linear-gradient(90deg, ${colors.gold}, ${colors.arcaneBlue})`,
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				icon: {
					color: colors.goldBright,
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					border: `1px solid ${alpha(colors.goldBright, 0.36)}`,
					boxShadow: `0 24px 70px ${alpha("#000", 0.62)}, 0 0 34px ${alpha(colors.arcanePurple, 0.16)}`,
				},
			},
		},
		MuiDialogTitle: {
			styleOverrides: {
				root: {
					borderBottom: `1px solid ${alpha(colors.gold, 0.18)}`,
					fontFamily: serifFont,
				},
			},
		},
		MuiDialogActions: {
			styleOverrides: {
				root: {
					borderTop: `1px solid ${alpha(colors.gold, 0.18)}`,
					padding: 16,
				},
			},
		},
		MuiMenu: {
			styleOverrides: {
				paper: {
					border: `1px solid ${alpha(colors.gold, 0.34)}`,
					backgroundImage: panelGradient,
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					color: colors.textPrimary,
					"&:hover, &.Mui-focusVisible": {
						backgroundColor: alpha(colors.gold, 0.13),
					},
					"&.Mui-selected": {
						backgroundColor: alpha(colors.arcanePurple, 0.34),
						"&:hover": {
							backgroundColor: alpha(colors.arcanePurple, 0.44),
						},
					},
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					minHeight: 40,
					borderBottom: `1px solid ${alpha(colors.gold, 0.2)}`,
				},
				indicator: {
					height: 2,
					backgroundColor: colors.goldBright,
					boxShadow: `0 0 12px ${alpha(colors.goldBright, 0.6)}`,
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					color: alpha(colors.textSecondary, 0.88),
					fontFamily: serifFont,
					fontWeight: 700,
					letterSpacing: 0,
					minHeight: 40,
					textTransform: "none",
					"&.Mui-selected": {
						color: colors.goldBright,
					},
					"&:focus-visible": {
						boxShadow: focusRing,
					},
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: alpha(colors.bgBlack, 0.96),
					border: `1px solid ${alpha(colors.gold, 0.42)}`,
					color: colors.textPrimary,
					fontFamily: bodyFont,
					fontSize: 13,
				},
				arrow: {
					color: alpha(colors.bgBlack, 0.96),
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderColor: alpha(colors.gold, 0.42),
					backgroundColor: alpha(colors.bgNavy, 0.74),
					color: colors.textPrimary,
				},
				deleteIcon: {
					color: alpha(colors.goldBright, 0.74),
					"&:hover": {
						color: colors.goldBright,
					},
				},
			},
		},
		MuiSwitch: {
			styleOverrides: {
				switchBase: {
					color: colors.textSecondary,
					"&.Mui-checked": {
						color: colors.goldBright,
						"& + .MuiSwitch-track": {
							backgroundColor: colors.gold,
							opacity: 0.62,
						},
					},
					"&.Mui-focusVisible .MuiSwitch-thumb": {
						boxShadow: focusRing,
					},
				},
				track: {
					backgroundColor: colors.borderDim,
					opacity: 0.78,
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					color: alpha(colors.textSecondary, 0.82),
					"&.Mui-checked": {
						color: colors.goldBright,
					},
					"&:focus-visible": {
						boxShadow: focusRing,
					},
				},
			},
		},
		MuiSlider: {
			styleOverrides: {
				root: {
					color: colors.gold,
				},
				rail: {
					backgroundColor: colors.borderDim,
					opacity: 0.9,
				},
				thumb: {
					border: `2px solid ${colors.goldBright}`,
					"&:focus-visible, &.Mui-focusVisible": {
						boxShadow: focusRing,
					},
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					border: `1px solid ${alpha(colors.gold, 0.26)}`,
					backgroundImage: panelGradient,
					color: colors.textPrimary,
				},
				icon: {
					color: "inherit",
				},
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					backgroundColor: alpha(colors.bgNavy, 0.5),
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					borderColor: alpha(colors.border, 0.55),
					color: colors.textSecondary,
					textTransform: "none",
					"&.Mui-selected": {
						backgroundColor: alpha(colors.gold, 0.18),
						color: colors.goldBright,
					},
					"&:focus-visible": {
						boxShadow: focusRing,
					},
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: alpha(colors.gold, 0.22),
				},
			},
		},
	},
});
